// DeleteAccountSheet.tsx — 앱 내 계정 삭제 (App Store 5.1.1(v))
//
// 애플은 "계정 생성이 가능한 앱은 앱 안에서 계정 삭제를 **시작**할 수 있어야 한다"고 요구한다.
// 웹으로 링크를 보내는 방식은 인정되지 않으므로 앱 안에서 끝까지 처리한다.
// 웹(app/mypage/settings)의 2단계 흐름을 그대로 옮겼다 — 사유 → 최종 확인.
//
// 서버 계약: delete_my_account(p_reasons text[], p_custom_reason text)
//   사유 기록과 삭제가 한 트랜잭션이라, 삭제가 실패하면 사유도 남지 않는다.
import { useEffect, useState } from 'react';
import { CSModal, CSBtn } from '../_components/cs';
import { supabase } from '../../lib/supabase';
import { unregisterPushToken } from '../../lib/push';
import { WITHDRAW_REASONS, normalizeWithdrawInput } from '@core/auth/withdrawReasons';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 삭제 성공 후 호출 — 캐시 비우기·홈 이동은 호출측 몫 */
  onDeleted: () => void;
};

export function DeleteAccountSheet({ open, onClose, onDeleted }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [custom, setCustom] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  // 활성 구독이 있으면 2단계에서 소멸 안내를 강조한다(효신님 결정: 막지 않고 경고 후 진행).
  const [hasActiveSub, setHasActiveSub] = useState(false);

  // 시트를 열 때마다 처음부터. 닫았다 다시 열었는데 2단계면 오폭 위험이 크다.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelected([]);
    setCustom('');
    setError('');
    setDeleting(false);

    let alive = true;
    // RLS가 본인 행만 통과시킨다(subs_select_own). 실패해도 기본 경고는 그대로 보여준다.
    supabase
      .from('subscriptions')
      .select('id')
      .in('status', ['active', 'past_due', 'grace'])
      .limit(1)
      .then(({ data }) => {
        if (alive) setHasActiveSub((data?.length ?? 0) > 0);
      }, () => {});
    return () => { alive = false; };
  }, [open]);

  function toggle(reason: string) {
    setSelected((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason],
    );
  }

  async function handleDelete() {
    setError('');
    setDeleting(true);
    const { reasons, customReason } = normalizeWithdrawInput(selected, custom);

    const { error: rpcError } = await supabase.rpc('delete_my_account', {
      p_reasons: reasons,
      p_custom_reason: customReason,
    });
    if (rpcError) {
      // 🔑 Supabase는 실패해도 throw가 아니라 { error }로 resolve한다 — 반환값 검사가 유일한 방어선.
      setError('삭제에 실패했어요. 잠시 후 다시 시도해주세요.');
      setDeleting(false);
      return;
    }

    // 계정이 사라졌으니 이 기기의 푸시 토큰도 파기한다.
    // ⚠ unregister는 인증이 필요하므로 signOut **전에** 불러야 한다(N-3에서 겪은 순서 함정).
    await unregisterPushToken().catch(() => {});
    await supabase.auth.signOut().catch(() => {});
    onDeleted();
  }

  if (!open) return null;

  const footer = step === 1 ? (
    <>
      <CSBtn variant="ghost" size="md" onClick={onClose}>취소</CSBtn>
      {/* 1단계는 아직 삭제가 아니라 사유 입력이다 — 빨강은 2단계 [영구 삭제]에만 쓴다.
          단 danger를 피하느라 primary까지 건너뛰면 주 행동이 아웃라인으로 약해진다(secondary는 5개 variant 중
          유일하게 border를 쓴다 · AI slop §G-②). 진행 버튼을 약하게 만들어 붙잡는 건 다크패턴이기도 하다. */}
      <CSBtn variant="primary" size="md" onClick={() => setStep(2)}>다음</CSBtn>
    </>
  ) : (
    <>
      <CSBtn variant="ghost" size="md" disabled={deleting} onClick={() => setStep(1)}>뒤로</CSBtn>
      <CSBtn variant="danger" size="md" disabled={deleting} onClick={handleDelete}>
        {deleting ? '삭제 중…' : '영구 삭제'}
      </CSBtn>
    </>
  );

  return (
    <CSModal
      open
      onClose={deleting ? () => {} : onClose}
      title={step === 1 ? '계정 삭제' : '정말 삭제할까요?'}
      footer={footer}
      sheet
    >
      <div style={{ padding: '4px 0 8px' }}>
        {step === 1 ? (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--cs-mute)', lineHeight: 1.55, marginBottom: 14 }}>
              떠나시는 이유를 알려주시면 더 나은 서비스로 개선할게요.<br />
              <span style={{ fontSize: 11, color: 'var(--cs-mute)' }}>입력한 내용은 익명 통계로만 쓰여요.</span>
            </div>
            {/* 경계는 테두리가 아니라 divider + 배경 명도차로 만든다 (AI slop 회피 design.md §G-② 경계 4대안).
                미선택 체크박스에 border를 쓰면 빈 사각형 6개가 세로로 늘어서 화면을 지배한다 — 2026-08-19 실기 지적. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {WITHDRAW_REASONS.map((reason, i) => {
                const on = selected.includes(reason);
                return (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => toggle(reason)}
                    aria-pressed={on}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '11px 2px', border: 'none', background: 'transparent',
                      // 항목 구분은 hairline divider로 — 마지막 항목은 textarea와 붙지 않게 생략
                      borderBottom: i < WITHDRAW_REASONS.length - 1 ? '1px solid var(--cs-line)' : 'none',
                      textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 13.5, color: on ? 'var(--cs-ink)' : 'var(--cs-ink-2)',
                      fontWeight: on ? 700 : 400,
                    }}
                  >
                    {/* 미선택도 배경을 채워 테두리 없이 존재를 드러낸다(#EFE9DC on #FEFCF8) */}
                    <span style={{
                      width: 17, height: 17, borderRadius: 5, flexShrink: 0,
                      background: on ? 'var(--cs-accent)' : 'var(--cs-surface-2)',
                      color: 'var(--cs-accent-ink)', fontSize: 11, fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>{on ? '✓' : ''}</span>
                    {reason}
                  </button>
                );
              })}
            </div>
            <textarea
              value={custom}
              onChange={(e) => setCustom(e.target.value.slice(0, 200))}
              placeholder="그 밖에 하고 싶은 말이 있다면 적어주세요 (선택)"
              rows={3}
              style={{
                width: '100%', marginTop: 12, padding: '10px 12px', resize: 'none',
                // border + background 동시 사용은 경계 이중 강조 → 배경 명도차만으로 충분(§G-②)
                borderRadius: 'var(--cs-r-md)', border: 'none',
                background: 'var(--cs-surface-2)', color: 'var(--cs-ink)',
                fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box',
              }}
            />
          </>
        ) : (
          <>
            <div style={{
              padding: '12px 14px', borderRadius: 'var(--cs-r-md)',
              background: 'var(--cs-surface-2)', marginBottom: 14,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cs-ink)', marginBottom: 8 }}>
                삭제하면 되돌릴 수 없어요
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, color: 'var(--cs-ink-2)', lineHeight: 1.7 }}>
                <li>BULL 잔액·크레딧·보유 아이템이 모두 사라져요</li>
                <li>모의투자 기록·복기·진단 결과가 삭제돼요</li>
                <li>같은 계정으로 다시 가입해도 복구되지 않아요</li>
                <li>커뮤니티에 남긴 글은 작성자 없이 남아요</li>
              </ul>
            </div>
            {hasActiveSub && (
              <div style={{
                padding: '11px 13px', borderRadius: 'var(--cs-r-md)',
                background: 'var(--cs-surface-2)', border: '1px solid var(--cs-line-2)',
                marginBottom: 14, fontSize: 12.5, color: 'var(--cs-ink-2)', lineHeight: 1.6,
              }}>
                <b style={{ color: 'var(--cs-ink)' }}>구독이 이용 중이에요.</b><br />
                삭제하면 남은 기간이 소멸되고 환불 신청도 할 수 없어요.
                환불을 원하시면 <b style={{ color: 'var(--cs-ink)' }}>삭제 전에</b> 환불규정에 따라 신청해주세요.
              </div>
            )}
            {error && (
              <div style={{ fontSize: 12.5, color: 'var(--cs-ink)', background: 'var(--cs-surface-2)',
                            padding: '10px 12px', borderRadius: 'var(--cs-r-md)', marginBottom: 12 }}>
                ⚠ {error}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: 'var(--cs-mute)', textAlign: 'center' }}>
              [영구 삭제]를 누르면 즉시 처리돼요
            </div>
          </>
        )}
      </div>
    </CSModal>
  );
}
